import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Calendar, Tag, Search, BookOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { APP_NAME } from '../config/branding';
import Footer from '../components/Footer';
import axios from 'axios';
import { API } from '../App';
import { Helmet } from '@dr.pogodin/react-helmet';

const CATEGORIES = [
  { name: "All", slug: "" },
  { name: "Collective Agreements", slug: "collective-agreements" },
  { name: "Contract Interpretation", slug: "contract-interpretation" },
  { name: "Labour Law Basics", slug: "labour-law-basics" },
  { name: "HR & Compliance", slug: "hr-compliance" },
  { name: "Arbitration & Grievances", slug: "arbitration-grievances" },
  { name: "Guides & Explainers", slug: "guides-explainers" },
  { name: "Case Examples", slug: "case-examples" },
];

const BlogIndex = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || '');
  const [totalPosts, setTotalPosts] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Add RSS autodiscovery link to head
  useEffect(() => {
    const rssLink = document.createElement('link');
    rssLink.rel = 'alternate';
    rssLink.type = 'application/rss+xml';
    rssLink.title = 'CBAIQ Blog RSS';
    rssLink.href = 'https://cbaiq.com/api/blog/feed.xml';
    rssLink.id = 'rss-autodiscovery';
    
    // Only add if not already present
    if (!document.getElementById('rss-autodiscovery')) {
      document.head.appendChild(rssLink);
    }
    
    return () => {
      const existing = document.getElementById('rss-autodiscovery');
      if (existing) existing.remove();
    };
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [activeCategory, currentPage]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('status', 'published');
      params.append('page', currentPage);
      params.append('page_size', 9);
      
      if (activeCategory) {
        params.append('category', activeCategory);
      }
      
      if (searchQuery) {
        params.append('q', searchQuery);
      }

      const response = await axios.get(`${API}/blog/posts?${params.toString()}`);
      const allPosts = response.data.posts || [];
      
      // First post is featured if no category filter and page 1
      if (!activeCategory && currentPage === 1 && allPosts.length > 0) {
        setFeaturedPost(allPosts[0]);
        setPosts(allPosts.slice(1));
      } else {
        setFeaturedPost(null);
        setPosts(allPosts);
      }
      
      setTotalPosts(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Failed to fetch blog posts:', error);
      setPosts([]);
      setFeaturedPost(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    if (searchQuery) {
      setSearchParams({ q: searchQuery, ...(activeCategory && { category: activeCategory }) });
    } else {
      setSearchParams(activeCategory ? { category: activeCategory } : {});
    }
    fetchPosts();
  };

  const handleCategoryChange = (slug) => {
    setActiveCategory(slug);
    setCurrentPage(1);
    if (slug) {
      setSearchParams({ category: slug, ...(searchQuery && { q: searchQuery }) });
    } else {
      setSearchParams(searchQuery ? { q: searchQuery } : {});
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background noise-bg">
      {/* RSS Autodiscovery */}
      <Helmet>
        <title>{APP_NAME} Blog - Labour Relations Insights</title>
        <meta name="description" content="Clear explanations of collective agreements, labour clauses, and workplace rights." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://cbaiq.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cbaiq.com/blog" />
        <meta property="og:title" content={`${APP_NAME} Blog - Labour Relations Insights`} />
        <meta property="og:description" content="Clear explanations of collective agreements, labour clauses, and workplace rights." />
        <meta property="og:site_name" content="CBAIQ" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${APP_NAME} Blog - Labour Relations Insights`} />
        <meta name="twitter:description" content="Clear explanations of collective agreements, labour clauses, and workplace rights." />
        <link rel="alternate" type="application/rss+xml" title="CBAIQ Blog RSS" href="https://cbaiq.com/api/blog/feed.xml" />
      </Helmet>

      {/* Header */}
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="font-heading font-semibold text-lg hover:text-primary transition-colors">
              {APP_NAME}
            </Link>
            <nav className="flex items-center gap-4">
              <Link to="/blog" className="text-sm font-medium text-primary">Blog</Link>
              <Link to="/pricing">
                <Button variant="ghost" size="sm">Pricing</Button>
              </Link>
              <Link to="/login">
                <Button variant="ghost" size="sm" data-testid="blog-login-btn">Log in</Button>
              </Link>
              <Link to="/register">
                <Button size="sm" data-testid="blog-get-started-btn">Get Started</Button>
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 px-4 border-b border-border">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <BookOpen className="w-6 h-6 text-primary" />
            <span className="text-sm font-medium tracking-widest uppercase text-muted-foreground">
              {APP_NAME} Blog
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold mb-4">
            Labour Relations Insights
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Clear explanations of collective agreements, labour clauses, and workplace rights.
          </p>
        </div>
      </section>

      {/* Search & Filters */}
      <section className="py-6 px-4 border-b border-border bg-secondary/20">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-3 py-1.5 text-sm rounded-sm transition-colors ${
                    activeCategory === cat.slug
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-card border border-border hover:border-primary/50'
                  }`}
                  data-testid={`category-${cat.slug || 'all'}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
            
            {/* Search */}
            <form onSubmit={handleSearch} className="flex gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="blog-search-input"
                />
              </div>
              <Button type="submit" variant="outline" data-testid="blog-search-btn">
                Search
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-12 px-4">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="space-y-8">
              {/* Featured skeleton */}
              <div className="bg-card border border-border rounded-md overflow-hidden">
                <div className="md:flex">
                  <Skeleton className="h-64 md:w-1/2" />
                  <div className="p-8 md:w-1/2 space-y-4">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-8 w-full" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                </div>
              </div>
              {/* Grid skeletons */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-card border border-border rounded-md overflow-hidden">
                    <Skeleton className="h-48" />
                    <div className="p-5 space-y-3">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-6 w-full" />
                      <Skeleton className="h-4 w-full" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : posts.length === 0 && !featuredPost ? (
            <div className="text-center py-16">
              <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-heading font-semibold mb-2">No articles found</h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery ? `No results for "${searchQuery}"` : 'Check back soon for new content.'}
              </p>
              {searchQuery && (
                <Button 
                  variant="outline" 
                  onClick={() => { setSearchQuery(''); handleCategoryChange(''); }}
                >
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-10">
              {/* Featured Post */}
              {featuredPost && (
                <Link 
                  to={`/blog/${featuredPost.slug}`}
                  className="block group"
                  data-testid="featured-post"
                >
                  <article className="bg-card border border-border rounded-md overflow-hidden card-interactive">
                    <div className="md:flex">
                      {featuredPost.featured_image_url ? (
                        <div className="md:w-1/2 h-64 md:h-auto bg-muted">
                          <img 
                            src={featuredPost.featured_image_url} 
                            alt={featuredPost.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="md:w-1/2 h-64 md:h-auto bg-gradient-to-br from-primary/10 to-accent flex items-center justify-center">
                          <BookOpen className="w-16 h-16 text-primary/30" />
                        </div>
                      )}
                      <div className="p-8 md:w-1/2 flex flex-col justify-center">
                        <div className="flex items-center gap-2 mb-3">
                          <Badge variant="secondary" className="text-xs">Featured</Badge>
                          {featuredPost.categories?.[0] && (
                            <span className="text-xs text-muted-foreground">
                              {CATEGORIES.find(c => c.slug === featuredPost.categories[0])?.name || featuredPost.categories[0]}
                            </span>
                          )}
                        </div>
                        <h2 className="font-heading text-2xl sm:text-3xl font-semibold mb-3 group-hover:text-primary transition-colors">
                          {featuredPost.title}
                        </h2>
                        {featuredPost.excerpt && (
                          <p className="text-muted-foreground mb-4 line-clamp-2">
                            {featuredPost.excerpt}
                          </p>
                        )}
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(featuredPost.published_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </article>
                </Link>
              )}

              {/* Post Grid */}
              {posts.length > 0 && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {posts.map((post) => (
                    <Link
                      key={post.id}
                      to={`/blog/${post.slug}`}
                      className="block group"
                      data-testid={`post-${post.slug}`}
                    >
                      <article className="bg-card border border-border rounded-md overflow-hidden h-full card-interactive">
                        {post.featured_image_url ? (
                          <div className="h-48 bg-muted">
                            <img 
                              src={post.featured_image_url} 
                              alt={post.title}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ) : (
                          <div className="h-48 bg-gradient-to-br from-secondary to-accent flex items-center justify-center">
                            <BookOpen className="w-10 h-10 text-primary/20" />
                          </div>
                        )}
                        <div className="p-5">
                          <div className="flex items-center gap-2 mb-2">
                            {post.categories?.[0] && (
                              <Badge variant="outline" className="text-xs">
                                {CATEGORIES.find(c => c.slug === post.categories[0])?.name || post.categories[0]}
                              </Badge>
                            )}
                          </div>
                          <h3 className="font-heading text-lg font-semibold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                            {post.title}
                          </h3>
                          {post.excerpt && (
                            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                              {post.excerpt}
                            </p>
                          )}
                          <div className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Calendar className="w-3 h-3" />
                            {formatDate(post.published_at)}
                          </div>
                        </div>
                      </article>
                    </Link>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 pt-6">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="flex items-center px-4 text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-secondary/30 border-t border-border">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold mb-4">
            Have a question about your agreement?
          </h2>
          <p className="text-muted-foreground mb-6">
            Upload your collective agreement and get instant, citation-backed answers.
          </p>
          <Link to="/register">
            <Button size="lg" className="btn-lift" data-testid="blog-cta-btn">
              Try {APP_NAME} Free <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogIndex;
